import React from "react";
import { Link } from "react-router-dom";
import { addReferralParam } from "@/utils/urlUtils";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { BadgePreview } from "./badge/BadgePreview";
import { EmbedCodeSection } from "./badge/EmbedCodeSection";
import { BadgeInstructions } from "./badge/BadgeInstructions";

interface ContractorBadgeProps {
  name: string;
  serviceType: string;
  pageUrl: string;
  showEmbedCode?: boolean;
}

export const ContractorBadge = ({ name, serviceType, pageUrl, showEmbedCode = false }: ContractorBadgeProps) => {
  // Ensure the URL starts with a forward slash for internal routing
  const normalizedPageUrl = pageUrl.startsWith('/') ? pageUrl : `/${pageUrl}`;
  
  // For external embedding, we need the full URL with domain
  const fullUrl = `https://homeprosokc.com${normalizedPageUrl}`;
  const badgeUrl = addReferralParam(fullUrl);
  
  console.log('ContractorBadge rendering with:', {
    name,
    serviceType,
    pageUrl,
    normalizedPageUrl,
    fullUrl,
    badgeUrl
  });
  
  const embedCode = `<a href="${badgeUrl}" target="_blank" rel="noopener noreferrer" style="display:block;max-width:300px;text-decoration:none;font-family:Inter,sans-serif">
  <div style="background:linear-gradient(135deg,#174996,#0d2b59);border-radius:12px;padding:20px;color:white;box-shadow:0 4px 15px rgba(23,73,150,0.2);border:2px solid rgba(255,255,255,0.1)">
    <div style="display:flex;align-items:center;gap:8px">
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="color:#ffd700"><path d="M6 9V2h12v7l-6 5.5L6 9Z"></path><path d="M8 4h8"></path><path d="M12 12v8"></path><path d="M8 20h8"></path></svg>
      <span style="font-size:14px;opacity:0.9;font-weight:500;letter-spacing:0.5px">Trusted by Home Pros OKC</span>
    </div>
  </div>
</a>`;

  if (!showEmbedCode) {
    return (
      <Link to={normalizedPageUrl}>
        <BadgePreview badgeUrl={normalizedPageUrl} />
      </Link>
    );
  }

  return (
    <div>
      <Link to={normalizedPageUrl}>
        <BadgePreview badgeUrl={normalizedPageUrl} />
      </Link>
      <Dialog>
        <DialogTrigger asChild>
          <Button variant="link" className="mt-4 text-sm text-primary hover:text-primary/80">
            Add badge to your website →
          </Button>
        </DialogTrigger>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Add this badge to your website</DialogTitle>
            <DialogDescription>
              Display this badge on your website to build trust with potential customers
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 max-h-[60vh] overflow-y-auto">
            <div>
              <h4 className="font-medium mb-2">Preview</h4>
              <div className="bg-gray-50 p-4 rounded-lg">
                <BadgePreview badgeUrl={badgeUrl} />
              </div>
            </div>

            <EmbedCodeSection embedCode={embedCode} />

            <div className="flex items-center gap-2 text-sm text-blue-600 bg-blue-50/50 px-4 py-2 rounded-lg">
              <span>↓</span>
              <span>Scroll down for step-by-step instructions</span>
            </div>

            <BadgeInstructions />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};